import {
  Archive,
  ArchiveRestore,
  ArrowUpRight,
  Bug,
  ChevronDown,
  CircleDot,
  Copy,
  Fingerprint,
  GitBranch,
  GitPullRequest,
  Hash,
  type LucideIcon,
  MonitorPlay,
  Rocket,
  SignalHigh,
  Tag,
  Tags,
  Timer,
  User,
  UserCheck,
  Users,
} from "lucide-react";
import { type ComponentProps, type ReactNode, useState } from "react";
import { toast } from "sonner";
import type { api } from "../../api";
import { personLabel } from "../../lib/people";
import { cn } from "../../lib/utils";
import type { IssueDetail, IssuesMeta } from "../../types";
import { PropertyList, PropertyRow } from "../property-list";
import { ReleaseProperty, ReleaseSummary } from "../releases/release-property";
import { TimeAgo } from "../time-ago";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { FactoryCard } from "./factory-card";
import {
  LabelChip,
  PRIORITY_LABELS,
  PriorityIcon,
  SlaPill,
  StatusDisc,
  TeamChip,
  TypeIcon,
  UserChip,
} from "./meta";
import { Picker } from "./picker";
import { PreviewEnvironment } from "./preview-environment";
import { SignOffCard } from "./sign-off";

type IssueUpdate = (patch: Parameters<typeof api.updateIssue>[1]) => void;

export function PropertiesSidebar({
  issue,
  meta,
  update,
  onChanged,
  onArchive,
  archivePending,
  draft = false,
  readOnly = false,
}: {
  issue: IssueDetail;
  meta: IssuesMeta | undefined;
  update: IssueUpdate;
  onChanged: () => void;
  onArchive: (archived: boolean) => void;
  archivePending: boolean;
  /** A blank issue that has not been created yet. */
  draft?: boolean;
  /** Users see the properties but cannot change them or reach the factory. */
  readOnly?: boolean;
}) {
  if (!meta) return null;
  // A hairline separates the column from the body; the rows themselves stay unboxed.
  return (
    <aside className="no-scrollbar fx-rise flex flex-col gap-6 *:shrink-0 xl:sticky xl:top-(--sticky-top,0.5rem) xl:max-h-[calc(100dvh-var(--sticky-top,0.5rem)-1.5rem)] xl:self-start xl:overflow-y-auto xl:border-l xl:p-px xl:pl-7">
      {!draft && <SignOffCard issue={issue} onChanged={onChanged} />}
      {!draft && !readOnly && <FactoryCard issue={issue} onChanged={onChanged} />}
      <IssueProperties
        issue={issue}
        meta={meta}
        update={update}
        onChanged={onChanged}
        draft={draft}
        readOnly={readOnly}
      />
      {!draft && <PreviewEnvironment issue={issue} onChanged={onChanged} />}
      {!draft && (
        <IssueLifecycle
          issue={issue}
          onArchive={onArchive}
          archivePending={archivePending}
          readOnly={readOnly}
        />
      )}
    </aside>
  );
}

type Row = { label: string; icon: LucideIcon; value: ReactNode };

/** Rows a collapsed property list keeps, in this order. */
const PRIMARY_PROPERTIES = ["Status", "Priority", "Type", "Reviewer"];

/**
 * The issue's editable fields as label/value rows. `collapsible` is the narrow-screen
 * layout: two columns under the title that show the primary rows until expanded.
 */
export function IssueProperties({
  issue,
  meta,
  update,
  onChanged,
  draft = false,
  readOnly = false,
  collapsible = false,
}: {
  issue: IssueDetail;
  meta: IssuesMeta | undefined;
  update: IssueUpdate;
  onChanged: () => void;
  draft?: boolean;
  readOnly?: boolean;
  collapsible?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!meta) return null;
  const status = meta.statuses.find((s) => s.key === issue.statusKey);
  const type = meta.types.find((t) => t.key === issue.typeKey);
  const repo = meta.repos.find((r) => r.key === issue.repoKey);
  // The work order already cloned the repo; a station cannot move mid-build.
  const repoLocked = readOnly || (!draft && Boolean(issue.taskId));
  const sentry = draft ? null : sentryLink(issue.externalRef);

  const rows: Row[] = [
    {
      label: "Status",
      icon: CircleDot,
      value: (
        <Picker
          readOnly={readOnly}
          multiple={false}
          options={meta.statuses.map((s) => ({
            value: s.key,
            label: s.name,
            icon: <StatusDisc category={s.category} color={s.color} />,
          }))}
          selected={[issue.statusKey]}
          onToggle={(key) => key !== issue.statusKey && update({ statusKey: key })}
        >
          <ValueButton>
            <StatusDisc category={status?.category} color={status?.color} />
            {status?.name ?? issue.statusKey}
          </ValueButton>
        </Picker>
      ),
    },
    {
      label: "Priority",
      icon: SignalHigh,
      value: (
        <Picker
          readOnly={readOnly}
          multiple={false}
          options={[0, 1, 2, 3, 4].map((value) => ({
            value: String(value),
            label: PRIORITY_LABELS[value]!,
            icon: <PriorityIcon priority={value} />,
          }))}
          selected={[String(issue.priority)]}
          onToggle={(value) => update({ priority: Number(value) })}
        >
          <ValueButton>
            <PriorityIcon priority={issue.priority} />
            {PRIORITY_LABELS[issue.priority]}
          </ValueButton>
        </Picker>
      ),
    },
    {
      label: "Type",
      icon: Tag,
      value: (
        <Picker
          readOnly={readOnly}
          multiple={false}
          options={meta.types.map((t) => ({
            value: t.key,
            label: t.name,
            icon: <TypeIcon typeKey={t.key} color={t.color} />,
          }))}
          selected={[issue.typeKey]}
          onToggle={(key) => update({ typeKey: key })}
        >
          <ValueButton>
            <TypeIcon typeKey={issue.typeKey} color={type?.color} />
            {type?.name ?? issue.typeKey}
          </ValueButton>
        </Picker>
      ),
    },
    {
      label: "Repo",
      icon: GitBranch,
      value: (
        <Picker
          readOnly={repoLocked}
          multiple={false}
          options={meta.repos
            .filter((r) => r.active || r.key === issue.repoKey)
            .map((r) => ({
              value: r.key,
              label: r.name,
              description: r.url.replace(/^https?:\/\//, ""),
              icon: <GitBranch className="size-3.5 text-muted-foreground" />,
            }))}
          selected={[issue.repoKey]}
          onToggle={(key) => key !== issue.repoKey && update({ repoKey: key })}
        >
          <ValueButton
            title={
              repoLocked && !readOnly && issue.taskId
                ? "On the factory line; the repo cannot change"
                : repo?.url
            }
          >
            <GitBranch className="size-3.5 text-muted-foreground" />
            {repo?.name ?? issue.repoKey}
            {repo && !repo.active && <span className="text-xs text-muted-foreground">inactive</span>}
          </ValueButton>
        </Picker>
      ),
    },
    {
      label: "Story points",
      icon: Hash,
      value: (
        <Picker
          readOnly={readOnly}
          multiple={false}
          options={["1", "2", "3", "5", "8", "13", "21", "34", "55", "89"].map((value) => ({
            value,
            label: value,
          }))}
          selected={issue.storyPoints != null ? [String(issue.storyPoints)] : []}
          onToggle={(value) =>
            update({ storyPoints: Number(value) === issue.storyPoints ? null : Number(value) })
          }
        >
          <ValueButton className="tabular-nums" muted={issue.storyPoints == null}>
            {issue.storyPoints ?? (readOnly ? "None" : "Add estimate")}
          </ValueButton>
        </Picker>
      ),
    },
    {
      label: "Reporter",
      icon: User,
      value: (
        <UserProperty
          users={meta}
          current={issue.reporter?.id ?? null}
          onPick={(id) => update({ reporterId: id })}
          display={issue.reporter}
          readOnly={readOnly}
        />
      ),
    },
    {
      label: "Reviewer",
      icon: UserCheck,
      value: (
        <UserProperty
          users={meta}
          current={issue.reviewer?.id ?? null}
          onPick={(id) => update({ reviewerId: id })}
          display={issue.reviewer}
          readOnly={readOnly}
          clearable
        />
      ),
    },
    {
      label: "Teams",
      icon: Users,
      value: (
        <Picker
          readOnly={readOnly}
          options={meta.teams.map((team) => ({ value: team.id, label: team.name }))}
          selected={issue.teams.map((team) => team.id)}
          onToggle={(id) => {
            const current = issue.teams.map((team) => team.id);
            update({
              teamIds: current.includes(id) ? current.filter((t) => t !== id) : [...current, id],
            });
          }}
        >
          <ChipsButton empty={readOnly ? "None" : "Add team"} count={issue.teams.length}>
            {issue.teams.map((team) => (
              <TeamChip key={team.id} name={team.name} color={team.color} />
            ))}
          </ChipsButton>
        </Picker>
      ),
    },
    {
      label: "Labels",
      icon: Tags,
      value: (
        <Picker
          readOnly={readOnly}
          options={meta.labels.map((label) => ({ value: label.id, label: label.name }))}
          selected={issue.labels.map((label) => label.id)}
          onToggle={(id) => {
            const current = issue.labels.map((label) => label.id);
            update({
              labelIds: current.includes(id) ? current.filter((l) => l !== id) : [...current, id],
            });
          }}
        >
          <ChipsButton empty={readOnly ? "None" : "Add labels"} count={issue.labels.length}>
            {issue.labels.map((label) => (
              <LabelChip key={label.id} name={label.name} color={label.color} />
            ))}
          </ChipsButton>
        </Picker>
      ),
    },
    {
      label: "SLA",
      icon: Timer,
      value: (
        <div className="flex min-h-7 flex-wrap items-center gap-x-2 gap-y-0.5">
          <SlaPill issue={issue} />
          {!issue.sla && <span className="text-xs text-muted-foreground">None</span>}
          {issue.slaDueAt && (
            <span className="text-xs text-muted-foreground tabular-nums">
              due {new Date(issue.slaDueAt).toLocaleString()}
            </span>
          )}
        </div>
      ),
    },
  ];
  if (!draft) {
    rows.push({
      label: "Pull request",
      icon: GitPullRequest,
      value: issue.prUrl ? (
        <a
          href={issue.prUrl}
          target="_blank"
          rel="noreferrer"
          className="flex min-h-7 min-w-0 items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <span className="truncate">{issue.prUrl.split("/").slice(-2).join("/")}</span>
          <ArrowUpRight className="size-3 shrink-0" />
        </a>
      ) : (
        <span className="flex min-h-7 items-center text-xs text-muted-foreground">None yet</span>
      ),
    });
    if (sentry) rows.push({ label: "Sentry", icon: Bug, value: <SentryLink link={sentry} /> });
    rows.push(
      {
        label: "Preview",
        icon: MonitorPlay,
        value: <PreviewDecision issue={issue} update={update} readOnly={readOnly} />,
      },
      {
        label: "Release",
        icon: Rocket,
        value: readOnly ? (
          <ReleaseSummary issue={issue} />
        ) : (
          <ReleaseProperty issue={issue} onChanged={onChanged} />
        ),
      },
      {
        label: "Identifier",
        icon: Fingerprint,
        value: (
          <button
            type="button"
            className="flex min-h-7 items-center gap-1.5 font-mono text-xs text-muted-foreground hover:text-foreground"
            onClick={() => {
              void navigator.clipboard.writeText(issue.identifier);
              toast.success("Copied");
            }}
          >
            {issue.identifier} <Copy className="size-3" />
          </button>
        ),
      },
    );
  }

  if (!collapsible) {
    return (
      <PropertyList aria-label="Properties">
        {rows.map((row) => (
          <PropertyRow key={row.label} label={row.label} icon={row.icon}>
            {row.value}
          </PropertyRow>
        ))}
      </PropertyList>
    );
  }

  const visible = expanded ? rows : rows.filter((row) => PRIMARY_PROPERTIES.includes(row.label));
  const hidden = rows.length - visible.length;
  return (
    <section aria-label="Properties">
      <PropertyList className="gap-x-8 sm:grid-cols-2">
        {visible.map((row) => (
          <PropertyRow key={row.label} label={row.label} icon={row.icon}>
            {row.value}
          </PropertyRow>
        ))}
      </PropertyList>
      <Button
        variant="ghost"
        size="sm"
        className="mt-1 -ml-2 h-7 px-2 text-xs text-muted-foreground"
        aria-expanded={expanded}
        onClick={() => setExpanded((open) => !open)}
      >
        <ChevronDown className={cn("size-3.5 transition-transform", expanded && "rotate-180")} />
        {expanded ? "Fewer properties" : `${hidden} more ${hidden === 1 ? "property" : "properties"}`}
      </Button>
    </section>
  );
}

/**
 * A property value that opens its picker: a quiet chip that only shows its edge on hover.
 * The rest of the props are spread so `PopoverTrigger asChild` can attach its ref and handlers.
 */
function ValueButton({
  children,
  className,
  muted = false,
  ...props
}: ComponentProps<typeof Button> & { muted?: boolean }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn(
        "-ml-2 h-7 max-w-full justify-start gap-2 px-2 font-normal",
        muted && "text-muted-foreground",
        className,
      )}
      {...props}
    >
      {children}
    </Button>
  );
}

/** Teams and labels: the chips when there are any, otherwise the prompt to add some. */
function ChipsButton({
  children,
  empty,
  count,
  className,
  ...props
}: ComponentProps<"button"> & { empty: string; count: number }) {
  return (
    <button
      type="button"
      className={cn(
        "-ml-2 flex min-h-7 max-w-full flex-wrap items-center gap-1 rounded-md px-2 py-1 text-left transition-colors hover:bg-accent",
        className,
      )}
      {...props}
    >
      {count > 0 ? children : <span className="text-xs text-muted-foreground">{empty}</span>}
    </button>
  );
}

/** Archive or restore, then when the issue was created, updated and archived. */
export function IssueLifecycle({
  issue,
  onArchive,
  archivePending,
  readOnly = false,
}: {
  issue: IssueDetail;
  onArchive: (archived: boolean) => void;
  archivePending: boolean;
  readOnly?: boolean;
}) {
  return (
    <div className="space-y-2">
      {!readOnly &&
        (issue.archivedAt ? (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            disabled={archivePending}
            onClick={() => onArchive(false)}
          >
            <ArchiveRestore /> Restore issue
          </Button>
        ) : (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="w-full" disabled={archivePending}>
                <Archive /> Archive issue
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Archive {issue.identifier}?</AlertDialogTitle>
                <AlertDialogDescription>
                  {issue.subIssues.total > 0
                    ? "It and its sub-issues leave the default lists and keep their status. Restoring it brings them back."
                    : "It leaves the default lists and keeps its status. You can restore it from the Archived tab."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction disabled={archivePending} onClick={() => onArchive(true)}>
                  Archive issue
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ))}
      <p className="text-xs text-muted-foreground">
        Created <TimeAgo at={issue.createdAt} /> · Updated <TimeAgo at={issue.updatedAt} />
        {issue.archivedAt ? (
          <>
            {" "}
            · Archived <TimeAgo at={issue.archivedAt} />
          </>
        ) : null}
      </p>
    </div>
  );
}

function UserProperty({
  users,
  current,
  onPick,
  display,
  clearable,
  readOnly,
}: {
  users: IssuesMeta;
  current: string | null;
  onPick: (id: string | null) => void;
  display: IssueDetail["reporter"];
  clearable?: boolean;
  readOnly?: boolean;
}) {
  const options = [
    ...(clearable ? [{ value: "__none__", label: "No one" }] : []),
    ...users.users.map((user) => ({ value: user.id, label: personLabel(user) })),
  ];
  return (
    <Picker
      readOnly={readOnly}
      multiple={false}
      options={options}
      selected={current ? [current] : []}
      onToggle={(value) => onPick(value === "__none__" ? null : value)}
    >
      <ValueButton muted={!display}>
        <UserChip user={display} />
        {display ? personLabel(display) : "No one"}
      </ValueButton>
    </Picker>
  );
}

interface SentryIssueLink {
  sentryIssueId: string;
  url: string;
  resolvedAt: string | null;
  resolveError: string | null;
}

/** The Sentry issue this one was created from, when there is one. */
function sentryLink(externalRef: Record<string, unknown> | null): SentryIssueLink | null {
  if (externalRef?.source !== "sentry" || typeof externalRef.sentryIssueId !== "string") return null;
  const url =
    typeof externalRef.url === "string"
      ? externalRef.url
      : typeof externalRef.eventUrl === "string"
        ? externalRef.eventUrl.replace(/\/events\/.*$/, "/")
        : null;
  if (!url) return null;
  return {
    sentryIssueId: externalRef.sentryIssueId,
    url,
    resolvedAt: typeof externalRef.resolvedAt === "string" ? externalRef.resolvedAt : null,
    resolveError: typeof externalRef.resolveError === "string" ? externalRef.resolveError : null,
  };
}

function SentryLink({ link }: { link: SentryIssueLink }) {
  return (
    <div className="flex min-h-7 flex-wrap items-center gap-2">
      <a
        href={link.url}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-1.5 text-sm text-primary hover:underline"
      >
        issues/{link.sentryIssueId}
        <ArrowUpRight className="size-3" />
      </a>
      {link.resolvedAt ? (
        <span className="text-xs text-muted-foreground">
          resolved <TimeAgo at={link.resolvedAt} />
        </span>
      ) : link.resolveError ? (
        <span className="text-xs text-destructive" title={link.resolveError}>
          not resolved
        </span>
      ) : null}
    </div>
  );
}

const PREVIEW_FIELD_LABEL: Record<string, string> = {
  null: "Undecided",
  true: "Required",
  false: "Not required",
};

/** The tri-state previewRequired field in the properties list. */
function PreviewDecision({
  issue,
  update,
  readOnly,
}: {
  issue: IssueDetail;
  update: (patch: Parameters<typeof api.updateIssue>[1]) => void;
  readOnly?: boolean;
}) {
  return (
    <Picker
      readOnly={readOnly}
      multiple={false}
      options={[
        { value: "null", label: "Undecided" },
        { value: "true", label: "Required" },
        { value: "false", label: "Not required" },
      ]}
      selected={[String(issue.previewRequired ?? null)]}
      onToggle={(value) => update({ previewRequired: value === "null" ? null : value === "true" })}
    >
      <ValueButton muted={issue.previewRequired == null}>
        {PREVIEW_FIELD_LABEL[String(issue.previewRequired ?? null)]}
      </ValueButton>
    </Picker>
  );
}
